'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

const contactDetails = [
  { icon: 'EnvelopeIcon', label: 'Email', value: 'contact@movio.in', href: 'mailto:contact@movio.in', target: '_self' },
  { icon: 'PhoneIcon', label: 'Phone', value: '+91 9600830488', href: 'tel:+919600830488', target: '_self' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp', value: 'Chat on WhatsApp', href: 'https://wa.me/919600830488', target: '_blank' },
  { icon: 'MapPinIcon', label: 'Office', value: 'Mariamman Kovil Street, Alangulam, Tamil Nadu 627851', href: '#', target: '_self' },
];

const inquiryTypes = [
  'Fleet Management Demo',
  'Logistics Platform Demo',
  'Fast Courier Setup',
  'White-label Partnership',
  'Technical Integration',
  'General Inquiry',
];

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  inquiry: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  inquiry?: string;
  message?: string;
}

const emptyForm: FormData = { name: '', email: '', phone: '', company: '', inquiry: '', message: '' };

function validate(data: FormData): FormErrors {
  const errors: FormErrors = {};
  if (!data.name.trim() || data.name.trim().length < 2) errors.name = 'Full name must be at least 2 characters.';
  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'Enter a valid email address.';
  if (data.phone && !/^[+]?[\d\s\-()]{7,15}$/.test(data.phone)) errors.phone = 'Enter a valid phone number.';
  if (!data.inquiry) errors.inquiry = 'Please select an inquiry type.';
  if (!data.message.trim() || data.message.trim().length < 10) errors.message = 'Message must be at least 10 characters.';
  return errors;
}

export default function ContactContent() {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState('');
  const [fadeOut, setFadeOut] = useState(false);

  const handleBackToForm = () => {
    setFadeOut(true);
    setTimeout(() => {
      setFormData(emptyForm);
      setErrors({});
      setTouched({});
      setStatus('idle');
      setServerError('');
      setFadeOut(false);
    }, 300);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [status]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const newErrors = validate({ ...formData, [field]: value });
      setErrors((prev) => ({ ...prev, [field]: newErrors[field] }));
    }
  };

  const handleBlur = (field: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const newErrors = validate(formData);
    setErrors((prev) => ({ ...prev, [field]: newErrors[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = Object.fromEntries(Object.keys(formData).map((k) => [k, true]));
    setTouched(allTouched as any);
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setStatus('loading');
    setServerError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setServerError(json.error || 'Something went wrong. Please try again.');
        setStatus('error');
      } else {
        setStatus('success');
      }
    } catch {
      setServerError('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  const inputClass = (field: keyof FormErrors) =>
    `form-input ${touched[field] && errors[field] ? 'border-red-400 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.12)]' : ''}`;

  return (
    <div className="pt-20">
      {/* Hero */}
      <PageHero className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              Get in Touch
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
              Let's build your{' '}
              <span className="italic font-light text-white/80">mobility brand.</span>
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto animate-on-scroll stagger-2">
              Book a demo, ask about pricing, or just say hello. We respond within 24 hours.
            </p>
          </div>
        </div>
      </PageHero>

      {/* Main content */}
      <div className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact info */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="animate-on-scroll stagger-1">
                <h2 className="font-display text-3xl font-semibold text-[#0F172A] mb-2">Contact us</h2>
                <p className="text-[#475569] leading-relaxed">
                  Our team is available Monday–Friday, 9am–7pm IST. For urgent support, use WhatsApp.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {contactDetails.map((c, i) => (
                  <div
                    key={c.label}
                    onClick={() => {
                      if (c.href !== '#') {
                        if (c.target === '_blank') {
                          window.open(c.href, '_blank', 'noopener,noreferrer');
                        } else {
                          window.location.href = c.href;
                        }
                      }
                    }}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && c.href !== '#') {
                        if (c.target === '_blank') {
                          window.open(c.href, '_blank', 'noopener,noreferrer');
                        } else {
                          window.location.href = c.href;
                        }
                      }
                    }}
                    className={`animate-on-scroll stagger-${i + 2} flex items-center gap-4 p-5 bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] shadow-card hover:shadow-card-hover hover:border-[rgba(0, 102, 255,0.2)] transition-all duration-300 group ${c.href !== '#' ? 'cursor-pointer' : ''}`}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                      style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}
                    >
                      <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0066FF]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#64748B] font-medium mb-0.5">{c.label}</div>
                      <div className="text-sm font-semibold text-[#0F172A]">{c.value}</div>
                    </div>
                    <Icon name="ArrowUpRightIcon" size={16} className="text-[#0066FF] ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <div
                className="animate-on-scroll stagger-6 rounded-2xl p-6 text-white"
                style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-white" />
                  <span className="font-semibold text-lg">WhatsApp Business</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Get instant responses via WhatsApp. Book a demo, ask questions, or get support in real time.
                </p>
                <a
                  href="https://wa.me/919600830488"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-[#128C7E] font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-white/90 transition-colors"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={16} />
                  Message Us Now
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {status === 'success' ? (
                <div
                  className="bg-white rounded-2xl p-12 border border-[rgba(15,23,42,0.07)] shadow-card text-center h-full flex flex-col items-center justify-center transition-opacity duration-300"
                  style={{ opacity: fadeOut ? 0 : 1 }}
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}
                  >
                    <Icon name="CheckCircleIcon" size={40} className="text-[#0066FF]" />
                  </div>
                  <h3 className="font-display text-3xl font-semibold text-[#0F172A] mb-3">Message Sent!</h3>
                  <p className="text-[#475569] text-lg max-w-sm mb-3">
                    Thank you, <strong>{formData.name}</strong>! We've received your message and will reply within 24 hours.
                  </p>
                  <p className="text-sm text-[#64748B] mb-8">
                    A confirmation has been sent to <strong>{formData.email}</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={handleBackToForm}
                    aria-label="Back to Contact Form"
                    className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-white transition-all duration-300 hover:opacity-90 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:ring-offset-2"
                    style={{ background: 'linear-gradient(135deg, #0066FF, #075DDF)' }}
                  >
                    <Icon name="ArrowLeftIcon" size={18} />
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="bg-white rounded-2xl p-8 md:p-10 border border-[rgba(15,23,42,0.07)] shadow-card flex flex-col gap-5"
                >
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-[#0F172A] mb-1">Send us a message</h2>
                    <p className="text-sm text-[#64748B]">Fill in the form and we'll be in touch shortly.</p>
                  </div>

                  {/* Server error banner */}
                  {status === 'error' && serverError && (
                    <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{serverError}</span>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass('name')}
                        placeholder="Vikram Reddy"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        onBlur={() => handleBlur('name')}
                        disabled={status === 'loading'}
                      />
                      {touched.name && errors.name && (
                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                          <Icon name="ExclamationCircleIcon" size={12} />
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className={inputClass('email')}
                        placeholder="vikram@company.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        disabled={status === 'loading'}
                      />
                      {touched.email && errors.email && (
                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                          <Icon name="ExclamationCircleIcon" size={12} />
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Phone Number</label>
                      <input
                        type="tel"
                        className={inputClass('phone')}
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        onBlur={() => handleBlur('phone')}
                        disabled={status === 'loading'}
                      />
                      {touched.phone && errors.phone && (
                        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                          <Icon name="ExclamationCircleIcon" size={12} />
                          {errors.phone}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Company Name</label>
                      <input
                        className="form-input"
                        placeholder="Your Company Pvt. Ltd."
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        disabled={status === 'loading'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2">
                      Inquiry Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass('inquiry')}
                      value={formData.inquiry}
                      onChange={(e) => handleChange('inquiry', e.target.value)}
                      onBlur={() => handleBlur('inquiry')}
                      disabled={status === 'loading'}
                    >
                      <option value="">Select inquiry type...</option>
                      {inquiryTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {touched.inquiry && errors.inquiry && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />
                        {errors.inquiry}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={inputClass('message')}
                      rows={4}
                      placeholder="Tell us about your business and what you're looking to build..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                      disabled={status === 'loading'}
                      style={{ resize: 'vertical' }}
                    />
                    {touched.message && errors.message && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="btn-primary px-8 py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2.5 w-full disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {status === 'loading' ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Icon name="PaperAirplaneIcon" size={18} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#64748B] text-center">
                    By submitting, you agree to our Privacy Policy. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map placeholder */}
      <div className="h-64 map-placeholder relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg, #0066FF, #075DDF)' }}
            >
              <Icon name="MapPinIcon" size={28} className="text-white" />
            </div>
            <p className="font-semibold text-[#0F172A]">Alangulam, Tamil Nadu</p>
            <p className="text-sm text-[#64748B]">India</p>
          </div>
        </div>
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-[#0066FF] opacity-20"
            style={{
              top: `${20 + (i * 17) % 60}%`,
              left: `${10 + (i * 23) % 80}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}